import { useEffect, useState } from "react";
import { api, fmtEur, fmtMese, COMPANY_COLORS, CATEGORIE_LABELS } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Car, AlertTriangle, Lock, Unlock, Inbox } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ScadenzeRiepilogo } from "@/components/ScadenzeRiepilogo";
import { PagamentiRiepilogo } from "@/components/PagamentiRiepilogo";
import { Link } from "react-router-dom";

const RicontrolliCard = ({ rows }) => (
  <Card className="p-4 rounded-md border-orange-500/60" data-testid="dash-ricontrolli">
    <div className="font-heading font-bold mb-2 flex items-center gap-2"><Inbox className="w-4 h-4 text-orange-500" />Da ricontrollare in Prima Nota</div>
    {rows.length === 0 ? <div className="text-sm text-muted-foreground" data-testid="dash-ricontrolli-empty">Niente da ricontrollare.</div> : (
      <div className="flex flex-wrap gap-2">
        {rows.map((r) => (
          <Link key={`${r.company}-${r.mese}`} to={`/prima-nota?mese=${r.mese}&tab=review`} data-testid={`dash-ricontrolli-${r.company}-${r.mese}`}
                className="border rounded-sm px-3 py-2 text-sm hover:bg-muted transition-colors">
            <div className="font-heading font-bold capitalize">{fmtMese(r.mese)} <span className="text-xs uppercase" style={{ color: COMPANY_COLORS[r.company] }}>{r.company}</span></div>
            <div className="text-xs text-muted-foreground">{r.righe} righe · <span className="font-mono">{fmtEur(r.importo)}</span> → apri</div>
          </Link>
        ))}
      </div>)}
  </Card>
);

export default function Dashboard() {
  const { company } = useCompany();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/summary", { params: { company } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [company]);

  if (!data) return <div className="text-muted-foreground">Caricamento…</div>;

  const chartData = data.spese_per_categoria.map((s) => ({
    name: CATEGORIE_LABELS[s.categoria] || s.categoria, importo: s.importo,
  })).sort((a, b) => b.importo - a.importo).slice(0, 10);

  const totSpese = data.spese_per_categoria.reduce((a, b) => a + b.importo, 0);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-heading font-black">Dashboard operativa</h1>
          <div className="text-sm text-muted-foreground">Mese di competenza: <span className="font-heading font-bold text-foreground">{fmtMese(data.mese_competenza)}</span></div>
        </div>
        {data.chiusura_corrente?.locked ? (
          <Badge className="bg-green-600 text-white gap-1 h-7"><Lock className="w-3 h-3" /> Mese chiuso</Badge>
        ) : (
          <Badge variant="outline" className="gap-1 h-7"><Unlock className="w-3 h-3" /> Mese aperto</Badge>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={<Users className="w-4 h-4" />} label="Padroncini attivi" value={data.total_padroncini} color="#2563EB" testid="stat-padroncini" />
        <StatCard icon={<Car className="w-4 h-4" />} label="Veicoli attivi" value={data.total_veicoli} color="#16A34A" testid="stat-veicoli" />
        <StatCard icon={<Inbox className="w-4 h-4" />} label="Coda eccezioni" value={data.coda_eccezioni} color="#F97316" testid="stat-eccezioni" />
        <StatCard icon={<Inbox className="w-4 h-4" />} label="Consumi da riportare" value={data.consumi_da_riportare} color="#7C3AED" testid="stat-consumi" />
        <StatCard icon={<AlertTriangle className="w-4 h-4" />} label="Scadenze prossime" value={data.scadenze.length} color="#E11D48" testid="stat-scadenze" />
      </div>

      <ScadenzeRiepilogo company={company} />
      <RicontrolliCard rows={data.ricontrolli || []} />
      <PagamentiRiepilogo company={company} />

      <div className="grid grid-cols-1 gap-4">
        <Card className="p-4 rounded-md">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-bold">Spese del mese per categoria</div>
              <div className="text-xs text-muted-foreground">Totale: <span className="font-mono">{fmtEur(totSpese)}</span></div>
            </div>
          </div>
          {chartData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">Nessuna spesa registrata per {fmtMese(data.mese_competenza)}</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 50 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => "€" + Math.round(v / 1000) + "k"} />
                  <Tooltip formatter={(v) => fmtEur(v)} />
                  <Bar dataKey="importo" radius={[2, 2, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={["#D95319", "#334155", "#2563EB", "#16A34A", "#EAB308"][i % 5]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      <Card className="p-4 rounded-md">
        <div className="font-heading font-bold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-destructive" />Padroncini con saldo negativo</div>
        {data.saldi_negativi.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Nessun saldo negativo. ✓</div>
        ) : (
          <table className="autotrans-table w-full">
            <thead><tr><th>Padroncino</th><th>Azienda</th><th className="text-right">Saldo</th></tr></thead>
            <tbody>
              {data.saldi_negativi.map((s) => (
                <tr key={s.padroncino_id} data-testid={`saldo-neg-${s.padroncino_id}`}>
                  <td>{s.nome}</td>
                  <td><Badge style={{ backgroundColor: COMPANY_COLORS[s.company], color: "white" }} className="h-5">{s.company}</Badge></td>
                  <td className="text-right font-mono text-destructive">{fmtEur(s.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value, color, testid }) {
  return (
    <Card className="p-3 rounded-md flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-sm flex items-center justify-center text-white" style={{ backgroundColor: color }}>{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-heading">{label}</div>
        <div className="text-xl font-heading font-black">{value}</div>
      </div>
    </Card>
  );
}
